import { PreCalculationResults } from './pre-calculation'
import { HeatingCalculationResults } from './heating'
import { CoolingCalculationResults } from './cooling'
import {
  createHourlyProvided,
  createSecondaryHourlyFromEnvironment,
  createSecondaryHourlyRequirement,
  HourlyEnergyFlow,
  PrimaryHourlyEnergyFlow,
} from '../../HourlyEnergyFlow'
import { EnergyType } from '../../EnergyFlow'
import { reduceToSum } from '../../../util/array-utility'
import { CirculationCalculationResults } from './circulation'
import type { HybridHeatPumpExtraOutputValue } from '../HybridHeatPumpProvider'
import {
  calculateExtraHeatingResults,
  calculateExtraHeatingCops,
  calculateExtraCoolingResults,
  calculateExtraCoolingEers,
  calculateExtraElectricityResults,
  calculateSourceUsages,
  calculateExtraTemperatureResults,
} from './extra-results'

export type ResultsCalculationParams = {
  preCalculationResults: PreCalculationResults
  circulationCalculationResults: CirculationCalculationResults
  heatingCalculationResults: HeatingCalculationResults
  coolingCalculationResults: CoolingCalculationResults
  providerId: string
  demands: PrimaryHourlyEnergyFlow[]
  isDhwDemand: (demandId: string) => boolean
}

export type ResultsCalculationResults = {
  flows: HourlyEnergyFlow[]
} & HybridHeatPumpExtraOutputValue

function calculateProvidedHeatingFlows(
  demands: PrimaryHourlyEnergyFlow[],
  isDhwDemand: (demandId: string) => boolean,
  preCalculationResults: PreCalculationResults,
  circulationCalculationResults: CirculationCalculationResults,
  heatingCalculationResults: HeatingCalculationResults,
) {
  const heatingDemands = demands.filter(
    (demand) => demand.energyType === EnergyType.Heating,
  )

  const basicHeatingDemands = heatingDemands.filter(
    (demand) => !isDhwDemand(demand.demandId),
  )
  const dhwHeatingDemands = heatingDemands.filter((demand) =>
    isDhwDemand(demand.demandId),
  )

  const basicPowerCoverageFactors =
    preCalculationResults.heating?.basic.powerCoverageFactors ??
    basicHeatingDemands.map(() => 0)
  const dhwPowerCoverageFactors =
    preCalculationResults.heating?.dhw.powerCoverageFactors ??
    dhwHeatingDemands.map(() => 0)

  const totalProvidedBasicHeating =
    circulationCalculationResults.heating.basic.providedHeating +
    heatingCalculationResults.basic.providedHeating
  const totalProvidedDhwHeating =
    circulationCalculationResults.heating.dhw.providedHeating +
    heatingCalculationResults.dhw.providedHeating

  const totalBasicPowerCoverageFactor =
    preCalculationResults.heating &&
    preCalculationResults.heating.basic.totalDemand > 0
      ? totalProvidedBasicHeating /
        preCalculationResults.heating.basic.totalDemand
      : 0
  const totalDhwPowerCoverageFactor =
    preCalculationResults.heating &&
    preCalculationResults.heating.dhw.totalDemand > 0
      ? totalProvidedDhwHeating / preCalculationResults.heating.dhw.totalDemand
      : 0

  const providedBasicHeatingFlows = basicHeatingDemands.map((demand, index) =>
    createHourlyProvided(
      demand.demandId,
      EnergyType.Heating,
      demand.value *
        (basicPowerCoverageFactors[index] ?? 0) *
        totalBasicPowerCoverageFactor,
    ),
  )
  const providedDhwHeatingFlows = dhwHeatingDemands.map((demand, index) =>
    createHourlyProvided(
      demand.demandId,
      EnergyType.Heating,
      demand.value *
        (dhwPowerCoverageFactors[index] ?? 0) *
        totalDhwPowerCoverageFactor,
    ),
  )

  return { providedBasicHeatingFlows, providedDhwHeatingFlows }
}

function calculateProvidedCoolingFlows(
  demands: PrimaryHourlyEnergyFlow[],
  circulationCalculationResults: CirculationCalculationResults,
  coolingCalculationResults: CoolingCalculationResults,
  preCalculationResults: PreCalculationResults,
) {
  const coolingDemands = demands.filter(
    (demand) => demand.energyType === EnergyType.Cooling,
  )

  const basicCirculationPower = !Number.isNaN(
    circulationCalculationResults.heating.basic.cop,
  )
    ? circulationCalculationResults.heating.basic.providedHeating *
      (1 - 1 / circulationCalculationResults.heating.basic.cop)
    : 0
  const dhwCirculationPower = !Number.isNaN(
    circulationCalculationResults.heating.dhw.cop,
  )
    ? circulationCalculationResults.heating.dhw.providedHeating *
      (1 - 1 / circulationCalculationResults.heating.dhw.cop)
    : 0

  const totalProvidedCooling =
    basicCirculationPower +
    dhwCirculationPower +
    coolingCalculationResults.freeCooling.providedCooling +
    coolingCalculationResults.active.providedCooling

  return coolingDemands.map((demand) => {
    if (
      !preCalculationResults.cooling ||
      preCalculationResults.cooling.totalDemand === 0
    ) {
      return createHourlyProvided(demand.demandId, EnergyType.Cooling, 0)
    }

    return createHourlyProvided(
      demand.demandId,
      EnergyType.Cooling,
      (demand.value * totalProvidedCooling) /
        (preCalculationResults.cooling?.totalDemand ?? 0),
    )
  })
}

function calculateRequirementFlows(
  providerId: string,
  totalRequiredElectricity: number,
) {
  return [
    createSecondaryHourlyRequirement(
      providerId,
      EnergyType.Electricity,
      totalRequiredElectricity,
    ),
  ]
}

function calculateFromEnvironmentFlows(
  providerId: string,
  providedBasicHeatingFlows: HourlyEnergyFlow[],
  providedDhwHeatingFlows: HourlyEnergyFlow[],
  providedCoolingFlows: HourlyEnergyFlow[],
  requirementFlows: HourlyEnergyFlow[],
) {
  const getTotal = (flows: HourlyEnergyFlow[]) =>
    flows.map((flow) => flow.value).reduce(reduceToSum, 0)

  return [
    createSecondaryHourlyFromEnvironment(
      providerId,
      EnergyType.Heating,
      getTotal(providedBasicHeatingFlows) +
        getTotal(providedDhwHeatingFlows) -
        getTotal(providedCoolingFlows) -
        getTotal(requirementFlows),
    ),
  ]
}

export function calculateResultsForHour({
  preCalculationResults,
  circulationCalculationResults,
  heatingCalculationResults,
  coolingCalculationResults,
  providerId,
  demands,
  isDhwDemand,
}: ResultsCalculationParams): ResultsCalculationResults {
  // Extra results

  const {
    recycledHeatingBasic,
    recycledHeatingDhw,
    heatingBasic,
    heatingDhw,
    totalHeating,
  } = calculateExtraHeatingResults(
    circulationCalculationResults,
    heatingCalculationResults,
  )

  const {
    recycledHeatingCopBasic,
    recycledHeatingCopDhw,
    heatingCopBasic,
    heatingCopDhw,
    totalHeatingCop,
  } = calculateExtraHeatingCops(
    circulationCalculationResults,
    heatingCalculationResults,
    totalHeating,
  )

  const { totalCooling, recycledCooling, freeCooling, activeCooling } =
    calculateExtraCoolingResults(
      circulationCalculationResults,
      coolingCalculationResults,
    )

  const { totalCoolingEer, freeCoolingEer, activeCoolingEer } =
    calculateExtraCoolingEers(coolingCalculationResults, totalCooling)

  const {
    electricityRequiredByTotal,
    electricityRequiredByHeating,
    electricityRequiredByCooling,
    electricityRequiredByRecyclingHeatingBasic,
    electricityRequiredByRecyclingHeatingDhw,
    electricityRequiredByHeatingBasic,
    electricityRequiredByHeatingDhw,
    electricityRequiredByFreeCooling,
    electricityRequiredByActiveCooling,
  } = calculateExtraElectricityResults(
    circulationCalculationResults,
    heatingCalculationResults,
    coolingCalculationResults,
  )

  const {
    temperatureHeatingBasic,
    temperatureHeatingDhw,
    temperatureCooling,
    heatCollectionTemperature,
  } = calculateExtraTemperatureResults(
    preCalculationResults,
    heatingCalculationResults,
  )

  const {
    totalSourceHeating,
    groundSourceHeating,
    airSourceHeating,
    wasteSourceHeating,
    energyBusSourceHeating,
    totalSourceCooling,
    groundSourceCooling,
    airSourceCooling,
    energyBusSourceCooling,
  } = calculateSourceUsages(
    heatingCalculationResults,
    coolingCalculationResults,
  )

  // Flows

  const { providedBasicHeatingFlows, providedDhwHeatingFlows } =
    calculateProvidedHeatingFlows(
      demands,
      isDhwDemand,
      preCalculationResults,
      circulationCalculationResults,
      heatingCalculationResults,
    )

  const providedCoolingFlows = calculateProvidedCoolingFlows(
    demands,
    circulationCalculationResults,
    coolingCalculationResults,
    preCalculationResults,
  )

  const requirementFlows = calculateRequirementFlows(
    providerId,
    electricityRequiredByTotal,
  )

  const fromEnvironmentFlows = calculateFromEnvironmentFlows(
    providerId,
    providedBasicHeatingFlows,
    providedDhwHeatingFlows,
    providedCoolingFlows,
    requirementFlows,
  )

  return {
    flows: [
      ...providedBasicHeatingFlows,
      ...providedDhwHeatingFlows,
      ...providedCoolingFlows,
      ...requirementFlows,
      ...fromEnvironmentFlows,
    ],

    totalHeating,
    recycledHeatingBasic,
    recycledHeatingDhw,
    heatingBasic,
    heatingDhw,

    totalHeatingCop,
    recycledHeatingCopBasic,
    recycledHeatingCopDhw,
    heatingCopBasic,
    heatingCopDhw,

    totalCooling,
    recycledCooling,
    freeCooling,
    activeCooling,

    totalCoolingEer,
    freeCoolingEer,
    activeCoolingEer,

    electricityRequiredByTotal,
    electricityRequiredByHeating,
    electricityRequiredByCooling,
    electricityRequiredByRecyclingHeatingBasic,
    electricityRequiredByRecyclingHeatingDhw,
    electricityRequiredByHeatingBasic,
    electricityRequiredByHeatingDhw,
    electricityRequiredByFreeCooling,
    electricityRequiredByActiveCooling,

    temperatureHeatingBasic,
    temperatureHeatingDhw,
    temperatureCooling,
    heatCollectionTemperature,

    totalSourceHeating,
    groundSourceHeating,
    airSourceHeating,
    wasteSourceHeating,
    energyBusSourceHeating,
    totalSourceCooling,
    groundSourceCooling,
    airSourceCooling,
    energyBusSourceCooling,

    remainingCapacity: coolingCalculationResults.powerCapacity,
  }
}
